import React, { Component } from 'react'
import CopyToClipboard from 'react-clipboard.js'
import type { Shortlist } from 'types/shortlist'
import type { User } from 'types/user'
import { getAliasedTracks } from '@disco/utils/aliases'
import DialogFooter from 'components/modals/dialog-footer'
import Button from 'components/ui/button'
import classNames from 'clsx'
import styles from './index.module.css'

export default class CopyTracklist extends Component<{
  format: string | null | undefined
  shortlist: {
    id: any
    groups: any
  } & Shortlist
  user: User
  onCopy: (...args: Array<any>) => any
  onClose: (...args: Array<any>) => any
}> {
  _handleCopy: () => void = () => {
    if (this.props.onCopy) {
      this.props.onCopy('Tracklist copied!')
    }
  }
  _handleFailedCopy: () => void = () => {
    if (this.props.onCopy) {
      this.props.onCopy()
    }
  }

  _getItemsInSections(
    renderSection: (
      section: Record<string, any> | null | undefined,
      items: Array<Record<string, any>>,
      offset: number,
    ) => string[],
  ): Array<Array<string>> {
    let playlistSections: string[][] = []
    const { shortlist, user } = this.props
    const { sections, items } = shortlist
    const aliasedTracks = getAliasedTracks(
      items.map(({ track }) => track),
      shortlist,
      user,
    ).map(track => ({
      track,
    }))

    // borrowed from pages/public-playlist/playlist
    if (sections) {
      playlistSections = sections.map((section, index) => {
        let sectionTracks = []

        if (index < sections.length - 1) {
          sectionTracks = aliasedTracks.slice(
            section.position,
            sections[index + 1].position,
          )
        } else {
          sectionTracks = aliasedTracks.slice(section.position)
        }

        return renderSection(section, sectionTracks, index + 1)
      })
    }

    if (sections && sections.length > 0 && sections[0].position > 0) {
      const sectionTracks = aliasedTracks.slice(0, sections[0].position)
      const ghostSection = renderSection(null, sectionTracks, 0)
      playlistSections.splice(0, 0, ghostSection)
    } else if (!sections || sections.length === 0) {
      const ghostSection = renderSection(null, aliasedTracks, 0)
      playlistSections.splice(0, 0, ghostSection)
    }

    return playlistSections
  }

  _getFormat(): string {
    const { format } = this.props
    return format || '{{track.artist}}\\t{{track.name}}'
  }

  _getPlainTextListing(): string {
    const format = this._getFormat()

    return this._getItemsInSections((section, tracks) =>
      (section ? [section.name] : []).concat(
        tracks.map(
          item =>
            format
              .replace(
                /{{track\.(\w+)}}/g,
                (_match, group1) => item.track[group1],
              )
              .replace('\\t', '\t'), // convenience
        ),
      ),
    )
      .map(line => line.join('\n'))
      .join('\n')
  }

  _renderTableRow(
    section: Record<string, any> | null | undefined,
    tracks: Array<Record<string, any>>,
    offset: number,
  ): Array<any> {
    let rows = []

    if (section) {
      rows = rows.concat([
        <tr key={`pane-${offset}-section-1`}>
          <td>
            <br />
          </td>
        </tr>,
        <tr key={`pane-${offset}-section-2`}>
          <td className={styles.copyTracklistListingSection} colSpan={3}>
            {section.name}
          </td>
        </tr>,
        <tr key={`pane-${offset}-section-3`}>
          <td>
            <br />
          </td>
        </tr>,
      ])
    }

    rows = rows.concat(
      tracks.map((item, index) => (
        <tr key={`pane-${offset}-item-${index}`}>
          <th
            data-unselectable-content={index + 1}
            className={styles.copyTracklistListingIndex}
          />
          <td>{item.track.artist}</td>
          <td>{item.track.name}</td>
        </tr>
      )),
    )
    return rows
  }

  _renderTableListing(): Array<Array<string>> {
    const res = this._getItemsInSections(this._renderTableRow.bind(this))

    return res
  }

  _renderTracklist(): React.ReactElement<'div'> {
    return (
      <div className={styles.copyTracklist}>
        <div className={styles.copyTracklistListingCont}>
          <table className={styles.copyTracklistListingTable}>
            <thead>
              <tr>
                <th
                  className={classNames(
                    styles.copyTracklistListingHead,
                    styles.copyTracklistListingIndex,
                  )}
                />
                <th className={styles.copyTracklistListingHead}>Artist</th>
                <th className={styles.copyTracklistListingHead}>Track title</th>
              </tr>
            </thead>
            <tbody>{this._renderTableListing()}</tbody>
          </table>
        </div>
        <DialogFooter
          primary={this._renderExpandedCopyBtn()}
          onClose={this.props.onClose}
        />
      </div>
    )
  }

  _renderExpandedCopyBtn(): React.ReactNode {
    return (
      <CopyToClipboard
        component="div"
        data-clipboard-text={this._getPlainTextListing()}
        onSuccess={this._handleCopy}
        onError={this._handleFailedCopy}
      >
        <Button appearance="primary" caption="Copy tracklist" />
      </CopyToClipboard>
    )
  }

  render(): React.ReactElement<'div'> {
    return this._renderTracklist()
  }
}
